import React from 'react';

// ============================================================================
// SVG Flowchart Primitives for Experiment Summaries
// ============================================================================

interface FlowNodeProps {
  x: number;
  y: number;
  width?: number;
  height?: number;
  title: string;
  subtitle?: string;
  color?: string;
  textColor?: string;
  onClick?: () => void;
}

export function FlowNode({
  x, y, width = 180, height = 56,
  title, subtitle, color = '#0d9488', textColor = '#fff',
}: FlowNodeProps) {
  const rx = 8;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={rx} fill="#1e293b" stroke={color} strokeWidth={2} />
      <rect x={x} y={y} width={width} height={22} rx={rx} fill={color} />
      <rect x={x} y={y + 14} width={width} height={8} fill={color} />
      <text x={x + width / 2} y={y + 15} textAnchor="middle" fill={textColor} fontSize={11} fontWeight={600}>{title}</text>
      {subtitle && (
        <text x={x + width / 2} y={y + 40} textAnchor="middle" fill="#94a3b8" fontSize={10}>
          {subtitle.length > 28 ? subtitle.slice(0, 26) + '...' : subtitle}
        </text>
      )}
    </g>
  );
}

interface FlowTerminalProps {
  x: number;
  y: number;
  width?: number;
  height?: number;
  label: string;
  color?: string;
}

export function FlowTerminal({
  x, y, width = 140, height = 36, label, color = '#6366f1',
}: FlowTerminalProps) {
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={height / 2} fill={color} opacity={0.9} />
      <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={600}>{label}</text>
    </g>
  );
}

interface FlowDecisionProps {
  x: number;
  y: number;
  size?: number;
  label: string;
  color?: string;
}

export function FlowDecision({
  x, y, size = 70, label, color = '#f59e0b',
}: FlowDecisionProps) {
  const half = size / 2;
  const points = `${x},${y + half} ${x + half},${y} ${x + size},${y + half} ${x + half},${y + size}`;
  return (
    <g>
      <polygon points={points} fill="#1e293b" stroke={color} strokeWidth={2} />
      <text x={x + half} y={y + half + 4} textAnchor="middle" fill="#e2e8f0" fontSize={9} fontWeight={500}>{label}</text>
    </g>
  );
}

interface FlowArrowProps {
  from: { x: number; y: number };
  to: { x: number; y: number };
  label?: string;
  color?: string;
  dashed?: boolean;
}

export function FlowArrow({
  from, to, label, color = '#64748b', dashed = false,
}: FlowArrowProps) {
  const id = `arrow-${from.x}-${from.y}-${to.x}-${to.y}`;
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  return (
    <g>
      <defs>
        <marker id={id} markerWidth={8} markerHeight={6} refX={8} refY={3} orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill={color} />
        </marker>
      </defs>
      <line
        x1={from.x} y1={from.y} x2={to.x} y2={to.y}
        stroke={color} strokeWidth={1.5}
        strokeDasharray={dashed ? '4 3' : undefined}
        markerEnd={`url(#${id})`}
      />
      {label && (
        <text x={midX + 4} y={midY - 4} fill="#94a3b8" fontSize={9} fontStyle="italic">{label}</text>
      )}
    </g>
  );
}

interface FlowNoteProps {
  x: number;
  y: number;
  text: string;
  width?: number;
  color?: string;
}

export function FlowNote({
  x, y, text, width = 120, color = '#f59e0b',
}: FlowNoteProps) {
  return (
    <g>
      <rect x={x} y={y} width={width} height={28} rx={4} fill="transparent" stroke={color} strokeWidth={1} strokeDasharray="3 2" />
      <text x={x + width / 2} y={y + 17} textAnchor="middle" fill={color} fontSize={9}>{text}</text>
    </g>
  );
}

interface FlowParallelProps {
  x: number;
  y: number;
  width?: number;
  height?: number;
  items: string[];
  color?: string;
}

export function FlowParallel({
  x, y, width = 200, items, color = '#0d9488',
}: FlowParallelProps) {
  const itemH = 28;
  const totalH = items.length * itemH + 8;
  return (
    <g>
      <rect x={x} y={y} width={width} height={totalH} rx={6} fill="#1e293b" stroke={color} strokeWidth={1.5} strokeDasharray="4 2" />
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <rect x={x + 6} y={y + 4 + i * itemH} width={width - 12} height={itemH - 4} rx={4} fill={color} opacity={0.15} />
          <text x={x + width / 2} y={y + 4 + i * itemH + 17} textAnchor="middle" fill="#e2e8f0" fontSize={10}>{item}</text>
        </React.Fragment>
      ))}
    </g>
  );
}

interface FlowContainerProps {
  width: number;
  height: number;
  children: React.ReactNode;
  title?: string;
}

export function FlowContainer({ width, height, children, title }: FlowContainerProps) {
  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: width, minWidth: 400 }}
      >
        <rect width={width} height={height} fill="#0f172a" rx={12} />
        {title && (
          <text x={width / 2} y={24} textAnchor="middle" fill="#94a3b8" fontSize={13} fontWeight={600}>
            {title}
          </text>
        )}
        {children}
      </svg>
    </div>
  );
}
